/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { clsx, type ClassValue } from "clsx";
import { formatDateInActiveLocale } from "@/lib/active-locale";
import { twMerge } from "tailwind-merge";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import {
  getProductDistribution,
  DEFAULT_DOWNLOAD_URL,
  CONSUMABLE_PRODUCT_IDS,
} from "@/lib/constants";
import { DOCUMENT_TRANSLATOR_PRODUCT_ID } from "@/lib/document-translator-pricing";
import i18n from "@/i18n";
import {
  annualLicenseLabel,
  monthlyLicenseLabel,
  withLicenseTypeSuffix,
} from "@/lib/license-type-label";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Generate a UUID v4 with cross-browser compatibility.
 *
 * Uses crypto.randomUUID() if available (modern browsers),
 * falls back to crypto.getRandomValues() for broader support,
 * and finally to Math.random() as last resort.
 *
 * @returns A UUID v4 string (e.g., "550e8400-e29b-41d4-a716-446655440000")
 */
export function generateUUID(): string {
  // Method 1: Use native crypto.randomUUID if available (modern browsers)
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    try {
      return crypto.randomUUID();
    } catch {
      // Fall through to next method
    }
  }

  // Method 2: Use crypto.getRandomValues (broader browser support)
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.getRandomValues === "function"
  ) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);

    // Set version (4) and variant (RFC4122) bits
    bytes[6] = (bytes[6] & 0x0f) | 0x40; // Version 4
    bytes[8] = (bytes[8] & 0x3f) | 0x80; // Variant RFC4122

    const hex = Array.from(bytes)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");

    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }

  // Method 3: Fallback using Math.random (last resort)
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Billing dates from Stripe (renewal, period start/end, invoice date, cancel
// date) are Unix timestamps that mark a precise UTC moment chosen by Stripe's
// billing-cycle math. They must be rendered as the UTC calendar date so the
// displayed value is stable and authoritative for every viewer. Without
// timeZone: "UTC", a viewer in a UTC-negative zone (e.g. the Americas) sees a
// period end of 2026-07-01 00:30 UTC as "30 Jun 2026" — a full day earlier
// than the real renewal date, which looks like a billing error to the customer.
// S487/Fix 4: Guard against null/undefined/0 timestamps. Stripe can return
// null for current_period_end in certain edge cases (e.g. incomplete subs).
// Without the guard, null * 1000 = 0 → "1 Jan 1970" shown as the renewal date.
export function formatDate(timestamp: number | null | undefined): string {
  if (!timestamp) return "—";
  const date = new Date(timestamp * 1000);
  if (isNaN(date.getTime())) return "—";
  // N-10 (S629): the shop's active language, not a fixed "en-GB". A German
  // customer reading translated words around a British date is the same defect
  // as one reading them around a United States price.
  return formatDateInActiveLocale(date, {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

export function formatDateFromDate(date: Date) {
  // N-10 (S629): same reader as formatDate above.
  return formatDateInActiveLocale(date, {
    year: "numeric",
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

// The shop's money formatter lives in its own module so that modules lib/utils
// itself imports (document-translator-pricing) and services that tests mock
// lib/utils around (currency-display) can use it without an import cycle or a
// mock hiding it. Re-exported here so every existing caller is unchanged.
export { formatCurrency, formatCurrencyCart } from "@/lib/money-format";

/**
 * AJ-2 (Pass 20): the label the download button should carry for a product.
 *
 * The reported defect was a label promising a file the system could not deliver.
 * Leaving one label over three behaviours would only narrow that, so the label
 * follows the destination.
 */
export const getDownloadActionLabel = (productId?: string): string => {
  const target = productId ? getProductDistribution(productId) : undefined;
  // P-5 (S629): a non-React module reads the engine directly. That is the
  // pattern the request describes and it is right here -- these labels are
  // computed outside a component, and the language switcher performs a full
  // reload, so there is no case where a stale label survives a change.
  if (target?.kind === "installer")
    return i18n.t("dashboard.products.actions.downloadInstaller");
  if (target?.kind === "store")
    return i18n.t("dashboard.products.actions.getFromStore");
  return i18n.t("dashboard.products.actions.openDownloadPage");
};

/**
 * PH-2 (S599): the same action, worded to fit a table cell.
 *
 * "Get from Microsoft Store" is 24 characters and was the widest thing in the
 * Actions column, which is what pushed the My Products table past its container
 * on tablet and laptop. The full wording is kept as the button's title so
 * nothing is lost -- it moves to hover rather than disappearing. Cards, which
 * have room, keep the full label.
 */
export const getDownloadActionLabelShort = (productId?: string): string => {
  const target = productId ? getProductDistribution(productId) : undefined;
  if (target?.kind === "installer")
    return i18n.t("dashboard.products.actions.installerShort");
  if (target?.kind === "store")
    return i18n.t("dashboard.products.actions.storeShort");
  return i18n.t("dashboard.products.actions.downloadShort");
};

/**
 * AJ-2: send the customer where this product actually lives.
 *
 * A Store listing, a download page and an installer are all opened in a new
 * tab. `a.download` is not what saves the installer: it is honoured only for
 * same-origin, blob and data addresses, and the installers are on the website
 * rather than the shop's origin, so Firefox and Safari ignore it and navigate
 * to the address while Chrome keeps it but drops the file-name hint. RF-8
 * (S607) is what makes an ignored attribute harmless -- with target set, the
 * navigation lands in the new tab instead of replacing the dashboard. What the
 * browser then does with the .exe is the website's to decide, through the
 * Content-Disposition header requested in W-63. The full reasoning is on the
 * anchor below; the dashboard stays open either way.
 */
export const handleDownloadClick = (productId?: string) => {
  const target = productId ? getProductDistribution(productId) : undefined;

  // No entry means no known destination. Silently landing the customer on the
  // generic downloads page under a button promising a file reads to them as a
  // failed download, which is the "redirected to Deskshare.com" report.
  if (!target) {
    toast.info(i18n.t("dashboard.products.downloadToast.title"), {
      description: i18n.t("dashboard.products.downloadToast.description"),
      action: {
        label: i18n.t("dashboard.products.downloadToast.action"),
        onClick: () =>
          window.open(DEFAULT_DOWNLOAD_URL, "_blank", "noopener,noreferrer"),
      },
    });
    return;
  }

  if (target.kind !== "installer") {
    window.open(target.url, "_blank", "noopener,noreferrer");
    return;
  }

  // RF-8 (S607): open the installer in a new tab.
  //
  // The installer addresses are on the website, which is a different origin from
  // the shop, and the download attribute does not survive a cross-origin link:
  // Firefox and Safari ignore it entirely and navigate to the address, and
  // Chrome has dropped the file-name hint since version 65. With no target set,
  // ignoring the attribute meant replacing the customer's dashboard.
  //
  // The attribute is kept because it still applies if an installer is ever
  // served from this origin, and it costs nothing where it is ignored.
  //
  // A reliable cross-origin download needs Content-Disposition: attachment on
  // the file itself. That is the website's to send, and it is now part of the
  // W-63 request rather than something this code can compensate for.
  const anchor = document.createElement("a");
  anchor.href = target.url;
  anchor.download = "";
  anchor.target = "_blank";
  anchor.rel = "noopener noreferrer";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
};

export const formatCartProductName = (name: string) => {
  if (!/ - | – /.test(name)) {
    return { main: name, rest: "" };
  }
  const [main, ...rest] = name.split(/ - | – /) || [];
  return {
    main,
    rest: rest.length > 0 ? rest.join(" - ") : "",
  };
};

/**
 * Append the license-type suffix to a base Stripe product name for display.
 *
 * S256 (FCR_ShoppingCart_Session255_v4 §3): centralises the license-type
 * suffix so every display surface shows the same string — "Auto FTP Manager
 * v8 - Annual License", "Text Speaker Live v4 - Monthly", and so on.
 * Previously the suffix was appended inline in `cart-product-row.tsx` for
 * the add-to-cart page only; every other surface (invoice table, product
 * card, product table, license table, license summary, subscription row,
 * device table, dashboard widgets) inherited the bare Stripe product name
 * and showed it without the license-type tag.
 *
 * Idempotent: if the suffix is already present in `productName` the
 * function returns `productName` unchanged.
 *
 * Pay-per-use and consumable products (Document Translator, TSL Extra
 * Words) have no license tier — the function returns the base name with
 * any trailing " - " / "-" / whitespace stripped, so names like
 * "Document Translator v2 - Price Per Word - " render cleanly.
 *
 * @param productName - The base Stripe product name (may be empty/nullish)
 * @param productType - Stripe `price.type`: "one_time" | "recurring"
 * @param interval    - Stripe `price.recurring.interval`: "year" | "month"
 * @param productId   - Stripe product ID for pay-per-use detection
 * @returns The display name with the license-type suffix appended
 *
 * @example
 * getProductDisplayName("Dictation Pro v2", "recurring", "year")
 *   // → "Dictation Pro v2 - Annual License"
 * getProductDisplayName("Text Speaker Live v4 - 45000 Words", "recurring", "month")
 *   // → "Text Speaker Live v4 - 45000 Words - Monthly"
 * getProductDisplayName("Document Translator v2 - Price Per Word - ",
 *                        "one_time", undefined, DOCUMENT_TRANSLATOR_PRODUCT_ID)
 *   // → "Document Translator v2 - Price Per Word"
 */
export function getProductDisplayName(
  productName: string,
  productType: "recurring" | "one_time" | undefined,
  interval?: string,
  productId?: string,
  priceLabel?: string,
): string {
  // SS-6 (FCR Pass 14): Prefer the price-level label (nickname or a label
  // derived from price metadata like word_count) over the Stripe product name.
  // Stripe's data model places tier identity (word count, interval) on the
  // price, not the product -- so "Text Speaker Live v4" from the product name
  // has no word count, while the price object does. When callers supply a
  // priceLabel it becomes the base so the suffix logic still applies correctly.
  const base = (priceLabel && priceLabel.trim()) ? priceLabel.trim() : (productName ?? "");
  // GP-8 (Pass 20): consumables are exempt because they have no licence tier.
  // Document Translator stays named explicitly as well as via the set, so the
  // rule survives a change to either.
  if (
    (productId !== undefined && CONSUMABLE_PRODUCT_IDS.has(productId)) ||
    productId === DOCUMENT_TRANSLATOR_PRODUCT_ID
  ) {
    // Strip any trailing " - " / "-" / whitespace left over from Stripe's
    // product naming (e.g., "Document Translator v2 - Price Per Word - ").
    return base.replace(/[\s-]+$/, "");
  }

  // PH-2 / N-9 (S637): the suffix is wording, not data, and it comes from the
  // one place that decides what a licence type is called.
  //
  // It looks like data because it is appended to a Stripe product name to make
  // "Dictation Pro - Annual License", and that composed string is then used as a
  // display name, as an invoice description and as a matching key. Because it
  // looked like data it was never treated as wording, which is why the branches
  // here were English while the code four lines below them already called the
  // translation engine.
  //
  // A one-time price carries no licence tier -- every one-time product the shop
  // sells is a consumable, exempted above -- so it gets no suffix.
  let suffix: string;
  if (productType === "recurring" && interval === "year") {
    suffix = annualLicenseLabel();
  } else if (productType === "recurring" && interval === "month") {
    suffix = monthlyLicenseLabel();
  } else {
    return base;
  }

  // PH-2 item 11 (S637): strip a trailing suffix in ANY known form -- the six
  // translations and the English original -- before appending. The plain
  // substring test this replaces asked whether a French name contained a French
  // suffix, while the name it was testing carried the English one.
  return withLicenseTypeSuffix(base, suffix);
}

/**
 * Parse discount breakdown JSON safely
 *
 * Parses the discount_breakdown JSON string from session/invoice metadata.
 * Returns null if parsing fails or input is invalid.
 *
 * @param discountBreakdownJson - JSON string from metadata.discount_breakdown
 * @returns Parsed DiscountBreakdown object or null
 *
 * @example
 * // Input: '{"prod_XXX":{"amount":1000,"pct":10}}'
 * // Output: { "prod_XXX": { amount: 1000, pct: 10 } }
 */
export function parseDiscountBreakdown(
  discountBreakdownJson: string | undefined | null,
): Record<string, { amount: number; pct: number }> | null {
  if (!discountBreakdownJson || typeof discountBreakdownJson !== "string") {
    return null;
  }

  try {
    const parsed = JSON.parse(discountBreakdownJson);

    // Validate structure: must be an object with product ID keys
    if (
      typeof parsed !== "object" ||
      parsed === null ||
      Array.isArray(parsed)
    ) {
      logger.warn("Invalid discount breakdown structure: not an object");
      return null;
    }

    // Validate each entry has amount and pct
    for (const [productId, entry] of Object.entries(parsed)) {
      const discountEntry = entry as Record<string, unknown>;
      if (
        typeof entry !== "object" ||
        entry === null ||
        typeof discountEntry.amount !== "number" ||
        typeof discountEntry.pct !== "number"
      ) {
        logger.warn(`Invalid discount entry for product ${productId}:`, entry);
        return null;
      }
    }

    return parsed as Record<string, { amount: number; pct: number }>;
  } catch (error) {
    logger.warn("Failed to parse discount breakdown JSON:", error);
    return null;
  }
}

/**
 * Get discount details for a specific product from the breakdown
 *
 * @param breakdown - Parsed discount breakdown object
 * @param productId - Stripe product ID to look up
 * @returns Discount entry or null if not found
 */
export function getProductDiscount(
  breakdown: Record<string, { amount: number; pct: number }> | null,
  productId: string | undefined,
): { amount: number; pct: number } | null {
  if (!breakdown || !productId) {
    return null;
  }
  return breakdown[productId] || null;
}
